/// <amd-module name="VSS/Platform/Navigation" />
import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";
import { IContributedIconDefinition } from "./ContributionTypes";
import { IVssComponentProperties } from "./Layout";
import { IParsedRoute, parseRouteTemplate } from "./Util/Url";

/**
 * These are flags that control behaviors for the navigation contributions.
 */
export const enum NavigationFlags {
    /**
     * This navigation contribution is a built-in part of the product.
     */
    BuiltIn = 0x0001,

    /**
     * This navigation contribution comes from a trusted source.
     */
    Trusted = 0x0002,

    /**
     * This navigation contribution should be disabled when this is set and when
     * shown, the user should not be able to interact with this contribution.
     */
    Disabled = 0x0010,

    /**
     * This navivgation contribution should not be shown in the UI when this is set.
     */
    Hidden = 0x0020,

    /**
     * This is a hint to the system that during layout, this contribution is
     * important and should not be hidden as space becomes less available.
     * This is not a requirement, just a hint.
     */
    NonCollapsible = 0x0040
}

/**
 * Information about a matched contributed route
 */
export interface IMatchedContributedRoute {
    /**
     * Id of the contributed route
     */
    routeId: string;

    /**
     * The parsed route templates for the route
     */
    parsedRoutes: IParsedRoute[];

    /**
     * The route templates for the route.
     */
    routeTemplates: string[];

    /**
     * The route values that were specified in the request
     */
    routeValues: { [key: string]: string };
}

/**
 * A navigation contribution is used to describe any high level object that is navigable within
 * the system. This can be things like a page, hub-group, hub, etc.
 */
export interface IContributedNavigation {
    /**
     * The contributionId of the navigation object.
     */
    id: string;

    /**
     * This is the contribution type.
     */
    type: string;

    /**
     * The order this contribution should be shown when all or a sub-set of the siblings are
     * shown in a presentation.
     */
    order: number;

    /**
     * The display string for the navigation contribution.
     */
    name?: string;

    /**
     * Attributes used to describe the general state of the navigation contribution.
     */
    flags?: number;

    /**
     * This is the URL for the location of this navigation contribution. Linking to it will
     * render a page with this contribution as the selected contribution.
     */
    href?: string;

    /**
     * If this navigation contribution can render a menu, it can specify a menu id which can be used
     * as target for contributions to this menu.
     */
    menuId?: string;

    /**
     * Navigation contributions MAY be grouped and when they are the group they are
     * associated with is defined by its navGroup.
     */
    navGroup?: string;

    /**
     * This will be the id of a parent navigation contribution if one exists.
     */
    parentId?: string;

    /**
     * This icon can be a URL to an image that represents the icon or an IContributedIconDefinition
     * which can specify different images based on the current theme.
     */
    icon?: string | IContributedIconDefinition;

    /**
     * Fabric Icon that represents the contributed navigation.
     */
    iconName?: string;

    /**
     * Asset of the icon that represents the contributed navigation. The URL can be retrieved
     * through the IVssLocationService.getAssetLocation method.
     */
    iconAsset?: string;

    /**
     * Navigation contribtions MAY use a custom component for the rendering of the component
     * if one is not specified, a standard component is used
     */
    componentType?: string;

    /**
     * When a custom component is being used to render the navigation contribution the
     * componentProperties are used to initialize the component.
     */
    componentProperties?: IVssComponentProperties;

    /**
     * If the navigation contribution has children, this array will contain references to
     * them in sorted order.
     */
    children?: IContributedNavigation[];

    /**
     * Set of names for the document types available on this navigation. This can be used
     * by other features to understand the context of the page.
     */
    displayedDocTypes?: string[];

    /**
     * Does the contributed navigation support mobile viewing
     */
    supportsMobile?: boolean;
}

/**
 * The navigation service is used to interact with all the navigation contributions
 * for the current page. This means being able to ask about the displayed contributions
 * and retrieve contributions based on their Ids.
 */
export interface IVssNavigationService extends IVssService {
    /**
     * Gets the information about the route that was matched for the currently displayed navigation.
     */
    getCurrentRoute(): IMatchedContributedRoute;

    /**
     * getDisplayedNavigation is used to retrieve the a given displayed contributions.
     * The 0th contribution is the top level and the last contibution is the leaf.
     */
    getDisplayedNavigation(): string[];

    /**
     * Given the id of a navigation contribution this will return the IContributedNavigation itself.
     */
    getNavigation(contributionId: string): IContributedNavigation | undefined;

    /**
     * Gets the string to use as the current page's document.title
     */
    getPageTitle(): string;

    /**
     * Get the route values for the request.
     */
    getRouteValues(): { [parameter: string]: string };

    /**
     * isDisplayed can be used to determine whether or not a given navigation
     * contribution being displayed.
     */
    isDisplayed(contributionId: string): boolean;
}

/**
 * NavigationDetails describes the structure of the data sent to the client from the
 * server through the navigation data provider.
 */
interface INavigationDetails {
    /**
     * This siteId that was determined for this request.
     */
    siteId: string;

    /**
     * The hierarchy defines the relationship of all contributions. Each contribution
     * that has child contributions will have an array of its children.
     */
    hierarchy: { [contributionId: string]: string[] };

    /**
     * This is the entire set of the contributed navigation objects that are reachable
     * from the current page.
     */
    navigation: { [contributionId: string]: IContributedNavigation };

    /**
     * This is the set of displayed contributions, this is an array with the root being
     * the contribution nearest the site and the last being the most relevant
     * contribution.
     */
    displayed: string[];

    /**
     * Set of locations for each of the navigation contributions.
     */
    location: { [contributionId: string]: string };

    /**
     * If specified, this indicates the url to update the browser to using replaceState.
     * The server may determine the actual URL to be different than the processed request.
     * This allows us to display the page, get the desired URL and not have to perform
     * a client side redirect.
     */
    resolvedUrl: string;

    /**
     * The title of the page (document.title)
     */
    pageTitle: string;

    /**
     * The id of the matched route for the currently displayed view
     */
    routeId: string;

    /**
     * The templates for the route that was matched for the currently displayed view
     */
    routeTemplates: string[];

    /**
     * The route values provided by the currently displayed view
     */
    routeValues: { [key: string]: string };
}

class VssNavigationService extends VssService implements IVssNavigationService {
    /**
     * Ensure that the resize event gets fired if the window's inner width changes due to the viewport meta changing
     * @param initialInnerWidth The window's inner width before the meta tag changed
     * @param timeout Give up trying after this many milliseconds
     */
    private static afterSetMetaViewport(initialInnerWidth: number) {
        const timeout = 1000;
        const startTime = Date.now();

        const callback = () => {
            if (initialInnerWidth !== window.innerWidth) {
                // The event must be fired later to avoid cyclical service starts
                window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
            } else {
                if (startTime + timeout > Date.now()) {
                    window.requestAnimationFrame(callback);
                }
            }
        };
        callback();
    }
    private navigationData: INavigationDetails;
    private matchedRoute: IMatchedContributedRoute;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        this.navigationData = contributionService.getData("ms.vss-web.navigation-data") as INavigationDetails;

        if (!this.navigationData) {
            return;
        }

        // If the request was "redirected" to another location, we will update our current state.
        let resolvedUrl = this.navigationData.resolvedUrl;
        if (resolvedUrl) {
            if (window.location.hash) {
                resolvedUrl += window.location.hash;
            }
            try {
                window.history.replaceState(history.state, document.title, resolvedUrl);
            } catch (ex) {
                console.error(`Failed to update URL to '${resolvedUrl}: ${ex.message}`);
            }
        }

        if (this.navigationData.navigation) {
            // Update the location for each of the navigation contributions.
            if (this.navigationData.location) {
                for (const contributionId in this.navigationData.navigation) {
                    const navigation = this.navigationData.navigation[contributionId];
                    navigation.href = this.navigationData.location[navigation.id];
                }
            }

            let supportsMobile = false;

            for (let i = this.navigationData.displayed.length - 1; i >= 0; i--) {
                supportsMobile = this.navigationData.navigation[this.navigationData.displayed[i]].supportsMobile || supportsMobile;
            }

            // Handle updating the <meta name="viewport" /> tag for pages that support mobile views
            let metaTag = document.querySelector("meta[name=viewport]") as HTMLMetaElement;

            // Create the meta tag and append it to <head> if it doesn't exist and we support mobile
            if (supportsMobile && !metaTag) {
                metaTag = document.createElement("meta");
                metaTag.name = "viewport";
                document.head.appendChild(metaTag);
            }

            const originalMetaContent = metaTag && metaTag.content;
            const newMetaContent = supportsMobile ? "width=device-width, initial-scale=1.0" : "";

            // Update the content attribute of the metatag if it is different, and initiate the callback that fires the resize event
            if (metaTag && originalMetaContent !== newMetaContent) {
                const initialInnerWidth = window.innerWidth;
                metaTag.content = newMetaContent;
                VssNavigationService.afterSetMetaViewport(initialInnerWidth);
            }

            // Update the children for each of the navigation contributions.
            // We need to filter out any that are "hidden", we need to sort
            // the children, and perform all other preperations before use.
            if (this.navigationData.hierarchy) {
                for (const parentId in this.navigationData.hierarchy) {
                    const parentNavigation = this.navigationData.navigation[parentId];
                    const children = this.navigationData.hierarchy[parentId];
                    const groups: { [navGroup: string]: number } = {};

                    const hiddenChildrenWithHrefs = [];

                    parentNavigation.children = [];

                    for (const child of children) {
                        const childNavigation = this.navigationData.navigation[child];

                        // Add 100000 to all Non-Trusted navigation.
                        if (((childNavigation.flags || 0) & NavigationFlags.Trusted) !== NavigationFlags.Trusted) {
                            childNavigation.order = (childNavigation.order || 0) + 100000;
                        }

                        if (childNavigation.flags !== undefined && (childNavigation.flags & NavigationFlags.Hidden) !== NavigationFlags.Hidden) {
                            childNavigation.parentId = parentNavigation.id;
                            parentNavigation.children.push(childNavigation);
                        } else if (childNavigation.href) {
                            hiddenChildrenWithHrefs.push(childNavigation);
                        }
                    }

                    // Navigation child sorting, first we sort by order, then group by navGroup.
                    parentNavigation.children.sort((child1, child2) => {
                        let result = child1.order - child2.order;
                        if (result === 0) {
                            result = child1.id.toUpperCase().localeCompare(child2.id.toUpperCase(), navigator.language);
                        }
                        return result;
                    });

                    // Ensure each navGroup defined in this set of children have an order Id.
                    let groupId = 1;
                    for (const childNavigation of parentNavigation.children) {
                        const navGroup = childNavigation.navGroup || "";
                        if (!groups[navGroup]) {
                            groups[navGroup] = groupId++;
                        }
                    }

                    // Navigation contribution sorting, first we sort by order, then group by navGroup.
                    parentNavigation.children.sort((child1, child2) => {
                        let result = groups[child1.navGroup || ""] - groups[child2.navGroup || ""];
                        if (result === 0) {
                            result = child1.order - child2.order;
                            if (result === 0) {
                                result = child1.id.localeCompare(child2.id, navigator.language);
                            }
                        }
                        return result;
                    });

                    // If no link is available for this contribution, we will use our first child that has an href.
                    if (!parentNavigation.href) {
                        for (const childNavigation of parentNavigation.children) {
                            if (childNavigation.href) {
                                parentNavigation.href = childNavigation.href;
                                break;
                            }
                        }

                        // if none of the child navigations have a href, look at the hidden children if there are any.
                        if (!parentNavigation.href && hiddenChildrenWithHrefs.length > 0) {
                            // sort the hidden children and keep in ascending order.
                            hiddenChildrenWithHrefs.sort((child1, child2) => {
                                return child1.order - child2.order;
                            });

                            // set to the child with the lowest order.
                            parentNavigation.href = hiddenChildrenWithHrefs[0].href;
                        }
                    }
                }

                // Populate information about the selected route
                this.matchedRoute = {
                    routeId: this.navigationData.routeId,
                    parsedRoutes: [],
                    routeTemplates: this.navigationData.routeTemplates,
                    routeValues: this.navigationData.routeValues
                };

                if (this.navigationData.routeTemplates) {
                    for (const routeTemplate of this.navigationData.routeTemplates) {
                        this.matchedRoute.parsedRoutes.push(parseRouteTemplate(routeTemplate));
                    }
                }
            }
        }
    }

    public getCurrentRoute(): IMatchedContributedRoute {
        return this.matchedRoute;
    }

    public getDisplayedNavigation(): string[] {
        return this.navigationData.displayed;
    }

    public getNavigation(contributionId: string): IContributedNavigation | undefined {
        return this.navigationData.navigation[contributionId];
    }

    public getRouteValues(): { [parameter: string]: string } {
        return this.navigationData.routeValues;
    }

    public isDisplayed(contributionId: string): boolean {
        return this.navigationData.displayed.indexOf(contributionId) != -1;
    }

    public getPageTitle(): string {
        return this.navigationData.pageTitle;
    }
}

Services.add("IVssNavigationService", { serviceFactory: VssNavigationService, options: ServiceOptions.Startup });

export function getCurrentRoute(pageContext: IVssPageContext): IMatchedContributedRoute {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    return navigationService.getCurrentRoute();
}

export function getDisplayedNavigation(pageContext: IVssPageContext): string[] {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    return navigationService.getDisplayedNavigation();
}

export function getNavigation(pageContext: IVssPageContext, contributionId: string): IContributedNavigation | undefined {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    return navigationService.getNavigation(contributionId);
}

export function getRouteValues(pageContext: IVssPageContext): { [parameter: string]: string } {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    return navigationService.getRouteValues();
}

export function isDisplayed(pageContext: IVssPageContext, contributionId: string): boolean {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    return navigationService.isDisplayed(contributionId);
}
